import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { datos } from '@/api/origen'
import { descargarArchivo } from '@/api/cliente'
import type {
  AperturaPrevia,
  AsientoDetalle,
  AsientoRegistrado,
  AsientoResumen,
  CajaContable,
  CentroCosto,
  ConfiguracionContable,
  CuentaContable,
  EstadoContabilidad,
  Hecho,
  LineaPedida,
  PeticionAsiento,
  Periodos,
  Recurrente,
  ReglaContable,
  ReporteContable,
  ReporteDisponible,
  Solicitud,
  TableroContable,
  VistaPreviaCatalogo,
} from './tipos'

/**
 * Los datos de la contabilidad. Todo va contra `/interna/contabilidad`, que
 * exige el complemento encendido y el permiso del rol: si falta uno, el
 * servidor dice cuál y la pantalla lo enseña tal cual.
 */

export const clavesContabilidad = {
  todo: ['contabilidad'] as const,
  estado: ['contabilidad', 'estado'] as const,
  configuracion: ['contabilidad', 'configuracion'] as const,
  tablero: ['contabilidad', 'tablero'] as const,
  cuentas: ['contabilidad', 'cuentas'] as const,
  centros: ['contabilidad', 'centros'] as const,
  reglas: ['contabilidad', 'reglas'] as const,
  periodos: ['contabilidad', 'periodos'] as const,
  asientos: (f: FiltroAsientos) => ['contabilidad', 'asientos', f] as const,
  asiento: (id: string) => ['contabilidad', 'asiento', id] as const,
  recurrentes: ['contabilidad', 'recurrentes'] as const,
  hechos: (estado: string | undefined) => ['contabilidad', 'hechos', estado ?? ''] as const,
  solicitudes: ['contabilidad', 'solicitudes'] as const,
  reportes: ['contabilidad', 'reportes'] as const,
  reporte: (codigo: string, p: ParametrosReporte) => ['contabilidad', 'reporte', codigo, p] as const,
  caja: ['contabilidad', 'caja'] as const,
}

export interface FiltroAsientos {
  desde?: string
  hasta?: string
  tipo?: string
  texto?: string
  cuenta?: string
}

export interface ParametrosReporte {
  desde?: string
  hasta?: string
  cuenta?: string
  nivel?: number
  comparar?: string
  desdeB?: string
  hastaB?: string
  centroCosto?: string
  sucursal?: string
}

/** Si el contribuyente tiene la contabilidad. Con un 403 (sin permiso) se da por no contratada. */
export function useEstadoContabilidad() {
  return useQuery({
    queryKey: clavesContabilidad.estado,
    queryFn: async ({ signal }) => {
      try {
        return await datos.obtener<EstadoContabilidad>('/contabilidad/estado', { senal: signal })
      } catch {
        return { contratada: false, iniciada: false, cajaConectada: false } satisfies EstadoContabilidad
      }
    },
    staleTime: 300_000,
  })
}

export function useConfiguracionContable() {
  return useQuery({
    queryKey: clavesContabilidad.configuracion,
    queryFn: ({ signal }) => datos.obtener<ConfiguracionContable>('/contabilidad/configuracion', { senal: signal }),
  })
}

export function useTableroContable() {
  return useQuery({
    queryKey: clavesContabilidad.tablero,
    queryFn: ({ signal }) => datos.obtener<TableroContable>('/contabilidad/tablero', { senal: signal }),
    // El motor trabaja en segundo plano: el tablero se refresca solo mientras se mira.
    refetchInterval: 30_000,
  })
}

export function useCuentas() {
  return useQuery({
    queryKey: clavesContabilidad.cuentas,
    queryFn: ({ signal }) => datos.obtener<CuentaContable[]>('/contabilidad/cuentas', { senal: signal }),
  })
}

export function useCentrosCosto() {
  return useQuery({
    queryKey: clavesContabilidad.centros,
    queryFn: ({ signal }) => datos.obtener<CentroCosto[]>('/contabilidad/centros-costo', { senal: signal }),
  })
}

export function useReglas() {
  return useQuery({
    queryKey: clavesContabilidad.reglas,
    queryFn: ({ signal }) => datos.obtener<ReglaContable[]>('/contabilidad/reglas', { senal: signal }),
  })
}

export function usePeriodosContables() {
  return useQuery({
    queryKey: clavesContabilidad.periodos,
    queryFn: ({ signal }) => datos.obtener<Periodos>('/contabilidad/periodos', { senal: signal }),
  })
}

export function useAsientos(filtro: FiltroAsientos) {
  return useQuery({
    queryKey: clavesContabilidad.asientos(filtro),
    queryFn: ({ signal }) =>
      datos.obtener<AsientoResumen[]>('/contabilidad/asientos', { consulta: { ...filtro }, senal: signal }),
  })
}

export function useAsiento(id: string | null) {
  return useQuery({
    queryKey: clavesContabilidad.asiento(id ?? ''),
    queryFn: ({ signal }) => datos.obtener<AsientoDetalle>(`/contabilidad/asientos/${encodeURIComponent(id ?? '')}`, { senal: signal }),
    enabled: id !== null,
  })
}

export function useRecurrentes() {
  return useQuery({
    queryKey: clavesContabilidad.recurrentes,
    queryFn: ({ signal }) => datos.obtener<Recurrente[]>('/contabilidad/recurrentes', { senal: signal }),
  })
}

export function useHechos(estado: string | undefined) {
  return useQuery({
    queryKey: clavesContabilidad.hechos(estado),
    queryFn: ({ signal }) => datos.obtener<Hecho[]>('/contabilidad/hechos', { consulta: { estado }, senal: signal }),
  })
}

export function useSolicitudes() {
  return useQuery({
    queryKey: clavesContabilidad.solicitudes,
    queryFn: ({ signal }) => datos.obtener<Solicitud[]>('/contabilidad/solicitudes', { senal: signal }),
    refetchInterval: (consulta) =>
      consulta.state.data?.some((s) => s.estado === 'pendiente' || s.estado === 'en_proceso') ? 3_000 : false,
  })
}

export function useReportesDisponibles() {
  return useQuery({
    queryKey: clavesContabilidad.reportes,
    queryFn: ({ signal }) => datos.obtener<ReporteDisponible[]>('/contabilidad/reportes', { senal: signal }),
    staleTime: 3_600_000,
  })
}

export function useReporte(codigo: string | null, parametros: ParametrosReporte) {
  return useQuery({
    queryKey: clavesContabilidad.reporte(codigo ?? '', parametros),
    queryFn: ({ signal }) =>
      datos.obtener<ReporteContable>(`/contabilidad/reportes/${encodeURIComponent(codigo ?? '')}`, {
        consulta: { ...parametros },
        senal: signal,
      }),
    enabled: codigo !== null,
  })
}

export function useCajaContable(habilitado: boolean) {
  return useQuery({
    queryKey: clavesContabilidad.caja,
    queryFn: ({ signal }) => datos.obtener<CajaContable>('/contabilidad/caja', { senal: signal }),
    enabled: habilitado,
  })
}

/** Una escritura que, al terminar, vuelve a pedir todo lo de la contabilidad. */
function useEscritura<V, R = unknown>(hacer: (v: V) => Promise<R>) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: hacer,
    onSuccess: () => cliente.invalidateQueries({ queryKey: clavesContabilidad.todo }),
  })
}

export function useIniciarContabilidad() {
  return useEscritura((v: { mesCierre: number; inicio: string; contabilizarDesde: string; plantilla: boolean }) =>
    datos.crear<{ cuentas: number; reglas: number; periodos: number }>('/contabilidad/iniciar', { cuerpo: v }),
  )
}

export function useGuardarConfiguracion() {
  return useEscritura((v: { mesCierre: number; contabilizarDesde: string; cajaActiva: boolean; cajaDesde: string | null }) =>
    datos.reemplazar('/contabilidad/configuracion', { cuerpo: v }),
  )
}

export function useCrearCuenta() {
  return useEscritura(
    (v: { codigo: string; nombre: string; padreId: string | null; clase: string | null; naturaleza: string; tipo: string; flujo: string | null; moneda: string | null }) =>
      datos.crear<{ id: string }>('/contabilidad/cuentas', { cuerpo: v }),
  )
}

export function useActualizarCuenta() {
  return useEscritura(
    (v: { id: string; codigo: string; nombre: string; naturaleza: string; tipo: string; flujo: string; moneda: string; activa: boolean }) =>
      datos.reemplazar(`/contabilidad/cuentas/${encodeURIComponent(v.id)}`, { cuerpo: v }),
  )
}

export function useGuardarCentroCosto() {
  return useEscritura((v: { codigo: string; nombre: string; activo: boolean }) => datos.reemplazar('/contabilidad/centros-costo', { cuerpo: v }))
}

export function useGuardarReglas() {
  return useEscritura((reglas: { rol: string; cuentaId: string | null }[]) => datos.reemplazar('/contabilidad/reglas', { cuerpo: { reglas } }))
}

export function useCompletarReglas() {
  return useEscritura(() => datos.crear<{ puestas: number }>('/contabilidad/reglas/completar'))
}

export function useCerrarPeriodo() {
  return useEscritura((id: string) => datos.crear(`/contabilidad/periodos/${encodeURIComponent(id)}/cerrar`))
}

export function useReabrirPeriodo() {
  return useEscritura((v: { id: string; motivo: string }) =>
    datos.crear(`/contabilidad/periodos/${encodeURIComponent(v.id)}/reabrir`, { cuerpo: { motivo: v.motivo } }),
  )
}

export function useCerrarAnio() {
  return useEscritura((anio: number) => datos.crear<{ asiento: string | null }>(`/contabilidad/anios/${anio}/cerrar`))
}

export function useReabrirAnio() {
  return useEscritura((v: { anio: number; motivo: string }) => datos.crear(`/contabilidad/anios/${v.anio}/reabrir`, { cuerpo: { motivo: v.motivo } }))
}

/**
 * Registrar un asiento a mano. La clave de idempotencia la da el formulario,
 * **una vez por formulario** (`useClaveIdempotencia`): un doble clic devuelve
 * el mismo asiento en vez de hacer dos.
 */
export function useRegistrarAsiento(claveIdempotencia: string) {
  return useEscritura((v: PeticionAsiento) =>
    datos.crear<AsientoRegistrado>('/contabilidad/asientos', { cuerpo: v, claveIdempotencia, reintentos: 2 }),
  )
}

export function useAnularAsiento() {
  return useEscritura((v: { id: string; motivo: string }) =>
    datos.crear<AsientoRegistrado>(`/contabilidad/asientos/${encodeURIComponent(v.id)}/anular`, { cuerpo: { motivo: v.motivo } }),
  )
}

export function useAdjuntar() {
  return useEscritura((v: { id: string; archivo: File }) => {
    const formulario = new FormData()
    formulario.append('archivo', v.archivo)
    return datos.crear<{ id: string }>(`/contabilidad/asientos/${encodeURIComponent(v.id)}/adjuntos`, { cuerpo: formulario })
  })
}

export function descargarAdjunto(asiento: string, adjunto: string): Promise<void> {
  return descargarArchivo(`/contabilidad/asientos/${encodeURIComponent(asiento)}/adjuntos/${encodeURIComponent(adjunto)}`)
}

export function useGuardarRecurrente() {
  return useEscritura(
    (v: { id?: string; nombre: string; descripcion: string; dia: number; proxima: string; hasta: string | null; activo: boolean; lineas: LineaPedida[] }) =>
      datos.crear<{ id: string }>('/contabilidad/recurrentes', { cuerpo: v }),
  )
}

export function useGenerarRecurrente() {
  return useEscritura((id: string) => datos.crear<AsientoRegistrado>(`/contabilidad/recurrentes/${encodeURIComponent(id)}/generar`))
}

export function useReintentarHecho() {
  return useEscritura((id: string) => datos.crear(`/contabilidad/hechos/${encodeURIComponent(id)}/reintentar`))
}

export function useContabilizarAhora() {
  return useEscritura((v: { desde?: string; hasta?: string }) => datos.crear<{ id: string }>('/contabilidad/contabilizar-ahora', { cuerpo: v }))
}

export function useImportarCatalogo() {
  return useEscritura((v: { archivo: File; aplicar: boolean }) => {
    const formulario = new FormData()
    formulario.append('archivo', v.archivo)
    return datos.crear<VistaPreviaCatalogo>('/contabilidad/cuentas/importar', { cuerpo: formulario, consulta: { aplicar: v.aplicar } })
  })
}

export function useImportarApertura() {
  return useEscritura((v: { archivo: File; aplicar: boolean; fecha: string | null }) => {
    const formulario = new FormData()
    formulario.append('archivo', v.archivo)
    return datos.crear<AperturaPrevia>('/contabilidad/apertura/importar', {
      cuerpo: formulario,
      consulta: { aplicar: v.aplicar, fecha: v.fecha },
    })
  })
}

export function descargarCatalogo(formato: 'xlsx' | 'csv' | 'pdf'): Promise<void> {
  return descargarArchivo('/contabilidad/cuentas/archivo', { formato })
}

export function descargarReporte(codigo: string, parametros: ParametrosReporte, formato: 'pdf' | 'xlsx' | 'csv'): Promise<void> {
  return descargarArchivo(`/contabilidad/reportes/${encodeURIComponent(codigo)}`, { ...parametros, formato })
}

